import { loadScriptByURL } from "./utils";

export const CALLPILOT_CHATBOT_CONFIG = {
  scriptUrl: "https://callpilot.qedrix.com/chatbot/widget.js",
  apiBaseUrl: "https://callpilot.qedrix.com",
  brandName: "EquityProtect",
  widgetTitle: "Property Protection Assistant",
  primaryColor: "#15803d",
  launcherLabel: "Ask a question",
};

export function isAdminPortalPath(pathname = "") {
  const path = String(pathname).toLowerCase();
  return (
    path.startsWith("/admin") ||
    path.startsWith("/admins") ||
    path.startsWith("/customer-service")
  );
}

const SERVICE_PROVIDER_PORTAL_PREFIXES = [
  "/documents",
  "/request-document",
  "/search-property",
  "/risk-assessments",
  "/jesse-protected-parties",
  "/clients",
  "/commissions",
  "/add-protection",
  "/client-onboarding",
  "/servicer-profile",
  "/servicer-properties",
  "/servicer-subscriptions",
  "/servicer-scans",
  "/servicer-documents",
  "/servicer-logs",
  "/servicer-allow-access",
  "/servicer-requested-documents",
];

export function isServiceProviderPortalPath(pathname = "") {
  const path = String(pathname).toLowerCase();
  return SERVICE_PROVIDER_PORTAL_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function shouldHideCallPilotChatbot(pathname = "") {
  const path = String(pathname).toLowerCase();
  return (
    isAdminPortalPath(path) ||
    path.includes("attorney-title-program") ||
    path.includes("industrypartner")
  );
}

export function shouldHideCallPilotLauncher(pathname = "") {
  return (
    shouldHideCallPilotChatbot(pathname) ||
    isServiceProviderPortalPath(pathname)
  );
}

export function initCallPilotChatbot(options = {}) {
  if (shouldHideCallPilotChatbot(window.location.pathname)) {
    return null;
  }

  if (!window.EquityProtectChatbot) {
    return null;
  }

  return window.EquityProtectChatbot.init({
    ...CALLPILOT_CHATBOT_CONFIG,
    ...options,
  });
}

export function getCallPilotChatbotInstance() {
  return window.EquityProtectChatbot?.getInstance?.() || null;
}

function setElementDisplay(el, visible) {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const next = visible ? "" : "none";
  if (el.style.display === next) {
    return false;
  }
  el.style.display = next;
  return true;
}

export function setCallPilotLauncherVisible(visible) {
  const root = document.querySelector('[data-equityprotect-chatbot="true"]');
  const launcher = root?.shadowRoot?.querySelector(".epcb-launcher");
  return setElementDisplay(launcher, visible);
}

export function setCallPilotChatbotVisible(visible) {
  const root = document.querySelector('[data-equityprotect-chatbot="true"]');
  const rootChanged = setElementDisplay(root, visible);
  // Keep launcher in sync with the host only when the host is being hidden.
  // Showing the host must not force the launcher visible — SP/admin shells hide
  // the floating button on purpose and drive chat from a sidebar control.
  const launcherChanged = visible
    ? false
    : setCallPilotLauncherVisible(false);
  return rootChanged || launcherChanged;
}

export function applyCallPilotVisibilityForPath(
  pathname = window.location.pathname,
) {
  const hideChatbot = shouldHideCallPilotChatbot(pathname);
  const hideLauncher =
    hideChatbot || shouldHideCallPilotLauncher(pathname);

  // Apply host + launcher in one pass. The previous show-then-hide launcher
  // sequence made CallPilot mutate the DOM, which retriggered the observer and
  // starved the main thread (same class of hang as the map OverlayView loop).
  const root = document.querySelector('[data-equityprotect-chatbot="true"]');
  setElementDisplay(root, !hideChatbot);
  setCallPilotLauncherVisible(!hideLauncher);
  return hideChatbot;
}

let adminHideObserverStarted = false;

export function startCallPilotAdminHideObserver() {
  if (adminHideObserverStarted || typeof MutationObserver === "undefined") {
    return;
  }

  adminHideObserverStarted = true;

  let scheduledFrame = 0;
  let applying = false;

  const syncVisibility = () => {
    scheduledFrame = 0;
    applying = true;
    try {
      applyCallPilotVisibilityForPath(window.location.pathname);
    } finally {
      // Ignore mutations we caused while writing display styles.
      queueMicrotask(() => {
        applying = false;
      });
    }
  };

  // Map tiles and large tables mutate the DOM continuously; coalescing to one
  // check per frame keeps the observer off the critical path.
  const scheduleSync = () => {
    if (applying || scheduledFrame) {
      return;
    }
    scheduledFrame = window.requestAnimationFrame(syncVisibility);
  };

  syncVisibility();

  const observer = new MutationObserver((mutations) => {
    if (applying) {
      return;
    }
    // CallPilot's own host/shadow updates must not re-enter the sync loop.
    const onlyChatbot = mutations.every((mutation) => {
      const target = mutation.target;
      if (!(target instanceof Node)) {
        return false;
      }
      const el =
        target.nodeType === Node.ELEMENT_NODE
          ? target
          : target.parentElement;
      return Boolean(
        el?.closest?.('[data-equityprotect-chatbot="true"]') ||
          el?.getAttribute?.("data-equityprotect-chatbot") === "true",
      );
    });
    if (onlyChatbot) {
      return;
    }
    scheduleSync();
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

export function openCallPilotChatbot() {
  if (shouldHideCallPilotChatbot(window.location.pathname)) {
    return false;
  }

  const instance = getCallPilotChatbotInstance();
  if (instance?.toggleOpen) {
    void instance.toggleOpen();
    return true;
  }
  return false;
}

export function ensureCallPilotChatbotReady({ hideLauncher = false } = {}) {
  if (shouldHideCallPilotChatbot(window.location.pathname)) {
    applyCallPilotVisibilityForPath(window.location.pathname);
    return Promise.resolve(null);
  }

  const finish = () => {
    if (!getCallPilotChatbotInstance()) {
      initCallPilotChatbot();
    }
    if (
      hideLauncher ||
      shouldHideCallPilotLauncher(window.location.pathname)
    ) {
      setCallPilotLauncherVisible(false);
    }
    return getCallPilotChatbotInstance();
  };

  if (window.EquityProtectChatbot) {
    return Promise.resolve(finish());
  }

  return new Promise((resolve) => {
    loadScriptByURL(
      "callpilot-chatbot",
      CALLPILOT_CHATBOT_CONFIG.scriptUrl,
      () => resolve(finish()),
    );
  });
}
